import { motion } from "framer-motion";
import "./Experience.css";

const EASE = [0.16, 1, 0.3, 1];

const experiences = [
  {
    role: "Lead Engineer / Software Engineering Lead Specialist",
    company: "Westpac Group",
    via: "via Simplus & Infosys Limited",
    period: "May 2021 — Present",
    current: true,
    projects: [
      {
        name: "BizEdge Strategic Origination Program",
        period: "Feb 2023 – Present",
        points: [
          "Led an 8-member team delivering microservices handling 1.5M+ transactions/day, reducing approval time by 32%",
          "Delivered platform modernisation impacting 10k+ business customers across Westpac group",
          "Transitioned platform to microservices backend with a responsive Micro Frontend on AWS",
          "Utilised Java Spring Boot, ReactJS, Single-Spa, and GenAI (Copilot) for development",
          "Maintained high code quality via SonarQube, Jest, JUnit, and TestNG",
        ],
      },
      {
        name: "Cash Flow Lending",
        period: "Sep 2022 – Jan 2023",
        points: [
          "Built backbone loan processing application for mortgage and business loan details",
          "Microservices via Java Spring Boot; responsive frontends via ReactJS",
          "Integrated Salesforce for email notifications and credit bureaus for credit history assessments",
        ],
      },
      {
        name: "Digital Credit Risk Assessment & Serviceability",
        period: "May 2021 – Aug 2022",
        points: [
          "Designed AWS Cloud Native components for comprehensive credit risk evaluation",
          "Built batch and streaming pipelines using Java Spring Boot and NodeJS",
          "Managed CI/CD pipelines and utilised Splunk for log analysis",
        ],
      },
    ],
    tech: [
      "Java",
      "Spring Boot",
      "ReactJS",
      "NodeJS",
      "AWS",

      // AI / GenAI (High-demand)
      "Machine Learning",
      "Deep Learning",
      "Natural Language Processing (NLP)",
      "Large Language Models (LLMs)",
      "GenAI",
      "Prompt Engineering",
      "Retrieval-Augmented Generation (RAG)",

      // Tools & Frameworks
      "LangChain",
      "PyTorch",
      "TensorFlow",
      "Hugging Face",
      "OpenAI API",

      // Data & Deployment
      "MLOps",
      "Model Deployment",
      "Vector Databases",
    ],
  },
  {
    role: "Developer",
    company: "Allianz Life Insurance",
    via: "via Tata Consultancy Services",
    period: "Jan 2019 — May 2021",
    current: false,
    projects: [
      {
        name: "Document Manager System",
        period: "Jan 2019 – May 2021",
        points: [
          "Streamlined enterprise document management handling substantial data volumes and high throughput",
          "Built end-to-end REST APIs using Java and Spring Boot",
          "Developed frontend application using ReactJS",
          "Managed CI/CD pipelines using AWS DevOps and Git",
        ],
      },
    ],
    tech: ["Java", "Spring Boot", "ReactJS", "REST APIs", "AWS DevOps", "Git"],
  },
];

export function Experience() {
  return (
    <section className="experience section" id="experience">
      <div className="section-container">
        <motion.div
          className="experience__header"
          initial={{ opacity: 0, y: 28 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.7, ease: EASE }}
        >
          <span className="section-label">Career</span>
          <h2 className="section-title">Work Experience</h2>
          <p className="section-subtitle">
            7+ years of progressive growth — from building enterprise systems at
            Allianz to leading cloud-native teams at Westpac Group.
          </p>
        </motion.div>

        <div className="timeline">
          {experiences.map((exp, i) => (
            <motion.div
              className="timeline__item"
              key={i}
              initial={{ opacity: 0, x: -32 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, ease: EASE, delay: i * 0.12 }}
            >
              <div className="timeline__marker">
                <div
                  className={`timeline__dot ${exp.current ? "timeline__dot--active" : ""}`}
                >
                  {exp.current && <div className="timeline__dot-pulse" />}
                </div>
                {i < experiences.length - 1 && (
                  <div className="timeline__line" />
                )}
              </div>

              <div className="timeline__card">
                <div className="timeline__card-header">
                  <div>
                    <h3 className="timeline__role">{exp.role}</h3>
                    <div className="timeline__meta">
                      <span className="timeline__company">{exp.company}</span>
                      <span className="timeline__via">{exp.via}</span>
                      <span className="timeline__separator">·</span>
                      <span className="timeline__period">{exp.period}</span>
                      {exp.current && (
                        <span className="timeline__badge-current">Current</span>
                      )}
                    </div>
                  </div>
                </div>

                <div className="timeline__projects">
                  {exp.projects.map((proj, j) => (
                    <div className="timeline__project" key={j}>
                      <div className="timeline__project-header">
                        <span className="timeline__project-name">
                          {proj.name}
                        </span>
                        <span className="timeline__project-period">
                          {proj.period}
                        </span>
                      </div>
                      <ul className="timeline__points">
                        {proj.points.map((point, k) => (
                          <li key={k} className="timeline__point">
                            <span className="timeline__point-bullet" />
                            {point}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>

                <div className="timeline__tech">
                  {exp.tech.map((t) => (
                    <span className="timeline__tech-tag" key={t}>
                      {t}
                    </span>
                  ))}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
